import React, { useState, useEffect } from 'react';
import { Play, Square, Radar, Activity, Clock, CheckCircle, XCircle, RefreshCw, Sparkles } from 'lucide-react';

const AffiliateFinderNoCost = () => {
  const [isRunning, setIsRunning] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState('idle');
  const [startTime, setStartTime] = useState(null);
  const [logs, setLogs] = useState([]);
  const [pollingInterval, setPollingInterval] = useState(null);
  const [selectedVertical, setSelectedVertical] = useState('');
  const [selectedGeo, setSelectedGeo] = useState('');
  const [availableGeos, setAvailableGeos] = useState([]);

  // Local Classification state
  const [isLocalClassifying, setIsLocalClassifying] = useState(false);
  const [isLocalLoading, setIsLocalLoading] = useState(false);
  const [localLogs, setLocalLogs] = useState([]);
  const [localPollingInterval, setLocalPollingInterval] = useState(null);

  // Affiliate Clean Reclassification state
  const [isAffiliateCleanRunning, setIsAffiliateCleanRunning] = useState(false);
  const [isAffiliateCleanLoading, setIsAffiliateCleanLoading] = useState(false);
  const [affiliateCleanLogs, setAffiliateCleanLogs] = useState([]);
  const [affiliateCleanPollingInterval, setAffiliateCleanPollingInterval] = useState(null);

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3000';

  // Fetch logs from backend
  const fetchLogs = async () => {
    try {
      const response = await fetch(`${apiUrl}/api/affiliate-finder-nocost/logs`, {
        method: 'GET', credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
      const result = await response.json();
      if (response.ok && result.success) setLogs(result.data);
    } catch (error) {
      console.error('Error fetching logs:', error);
    }
  };

  // Fetch local classification logs (shared with original affiliate finder)
  const fetchLocalLogs = async () => {
    try {
      const response = await fetch(`${apiUrl}/api/affiliate-finder/local-classify/logs`, {
        method: 'GET', credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
      const result = await response.json();
      if (response.ok && result.success) setLocalLogs(result.data);
    } catch (error) {
      console.error('Error fetching local logs:', error);
    }
  };

  // Fetch affiliate clean reclassification logs (shared with original)
  const fetchAffiliateCleanLogs = async () => {
    try {
      const response = await fetch(`${apiUrl}/api/affiliate-finder/affiliate-clean/logs`, {
        method: 'GET', credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
      const result = await response.json();
      if (response.ok && result.success) setAffiliateCleanLogs(result.data);
    } catch (error) {
      console.error('Error fetching affiliate clean logs:', error);
    }
  };

  // Fetch available geos
  const fetchAvailableGeos = async () => {
    try {
      const response = await fetch(`${apiUrl}/api/affiliate-finder-nocost/available-geos`, {
        method: 'GET', credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
      const result = await response.json();
      if (response.ok && result.success) setAvailableGeos(result.data);
    } catch (error) {
      console.error('Error fetching geos:', error);
    }
  };

  useEffect(() => {
    fetchAvailableGeos();
  }, []);

  // Handle start/stop
  const handleAction = async (action) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${apiUrl}/api/affiliate-finder-nocost`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, vertical: selectedVertical, geo: selectedGeo || null })
      });
      const result = await response.json();

      if (response.ok && result.success) {
        setIsRunning(action === 'start');
        setStatus(action === 'start' ? 'running' : 'stopped');

        if (action === 'start') {
          setStartTime(new Date());
          const interval = setInterval(fetchLogs, 2000);
          setPollingInterval(interval);
        } else {
          setStartTime(null);
          if (pollingInterval) {
            clearInterval(pollingInterval);
            setPollingInterval(null);
          }
        }
        fetchLogs();
      } else {
        console.error(`Failed to ${action} affiliate finder:`, result.error || result.message);
      }
    } catch (error) {
      console.error(`Error ${action}ing affiliate finder:`, error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle local classification start/stop/resume (uses shared endpoint)
  const handleLocalClassification = async (action) => {
    setIsLocalLoading(true);
    try {
      const response = await fetch(`${apiUrl}/api/affiliate-finder/local-classify`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action })
      });
      const result = await response.json();

      if (response.ok && result.success) {
        setIsLocalClassifying(action === 'start' || action === 'resume');
        if (action === 'start' || action === 'resume') {
          const interval = setInterval(fetchLocalLogs, 2000);
          setLocalPollingInterval(interval);
        } else {
          if (localPollingInterval) {
            clearInterval(localPollingInterval);
            setLocalPollingInterval(null);
          }
        }
        fetchLocalLogs();
      } else {
        console.error(`Failed to ${action} local classification:`, result.error || result.message);
      }
    } catch (error) {
      console.error(`Error ${action}ing local classification:`, error);
    } finally {
      setIsLocalLoading(false);
    }
  };

  // Handle affiliate clean reclassification start/stop (uses shared endpoint)
  const handleAffiliateClean = async (action) => {
    setIsAffiliateCleanLoading(true);
    try {
      const response = await fetch(`${apiUrl}/api/affiliate-finder/affiliate-clean`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action })
      });
      const result = await response.json();

      if (response.ok && result.success) {
        setIsAffiliateCleanRunning(action === 'start');
        if (action === 'start') {
          const interval = setInterval(fetchAffiliateCleanLogs, 2000);
          setAffiliateCleanPollingInterval(interval);
        } else {
          if (affiliateCleanPollingInterval) {
            clearInterval(affiliateCleanPollingInterval);
            setAffiliateCleanPollingInterval(null);
          }
        }
        fetchAffiliateCleanLogs();
      } else {
        console.error(`Failed to ${action} affiliate clean:`, result.error || result.message);
      }
    } catch (error) {
      console.error(`Error ${action}ing affiliate clean:`, error);
    } finally {
      setIsAffiliateCleanLoading(false);
    }
  };

  // Running time
  const [runningTime, setRunningTime] = useState('00:00:00');

  useEffect(() => {
    if (isRunning && startTime) {
      const interval = setInterval(() => {
        const diff = new Date() - startTime;
        const hours = Math.floor(diff / 3600000).toString().padStart(2, '0');
        const minutes = Math.floor((diff % 3600000) / 60000).toString().padStart(2, '0');
        const seconds = Math.floor((diff % 60000) / 1000).toString().padStart(2, '0');
        setRunningTime(`${hours}:${minutes}:${seconds}`);
      }, 1000);
      return () => clearInterval(interval);
    } else {
      setRunningTime('00:00:00');
    }
  }, [isRunning, startTime]);

  // Cleanup polling on unmount
  useEffect(() => { return () => { if (pollingInterval) clearInterval(pollingInterval); }; }, [pollingInterval]);
  useEffect(() => { return () => { if (localPollingInterval) clearInterval(localPollingInterval); }; }, [localPollingInterval]);
  useEffect(() => { return () => { if (affiliateCleanPollingInterval) clearInterval(affiliateCleanPollingInterval); }; }, [affiliateCleanPollingInterval]);

  return (
    <div className="p-6 space-y-6 dark:bg-slate-950">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-slate-100">Affiliate Finder (No Cost)</h1>
          <p className="text-gray-600 dark:text-slate-400 mt-1">Discover and track potential affiliates automatically using local browser</p>
        </div>
        <div className="flex items-center gap-2">
          <Radar className={`w-6 h-6 ${isRunning ? 'text-green-600 animate-pulse' : 'text-gray-400'}`} />
          <span className={`text-sm font-medium ${isRunning ? 'text-green-600' : 'text-gray-500'}`}>
            {status === 'running' ? 'Active' : status === 'stopped' ? 'Stopped' : 'Idle'}
          </span>
        </div>
      </div>

      {/* Status Card */}
      <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Status */}
          <div className="space-y-2">
            <div className="text-sm text-gray-500 dark:text-slate-400">Status</div>
            <div className="flex items-center gap-2">
              {isRunning ? (
                <Activity className="w-5 h-5 text-green-600" />
              ) : (
                <Square className="w-5 h-5 text-gray-400" />
              )}
              <span className="text-2xl font-bold text-gray-900 dark:text-slate-100">
                {isRunning ? 'Running' : 'Stopped'}
              </span>
            </div>
          </div>

          {/* Running Time */}
          <div className="space-y-2">
            <div className="text-sm text-gray-500 dark:text-slate-400">Running Time</div>
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-blue-600" />
              <span className="text-2xl font-bold text-gray-900 dark:text-slate-100 font-mono">
                {runningTime}
              </span>
            </div>
          </div>

          {/* Affiliates Found */}
          <div className="space-y-2">
            <div className="text-sm text-gray-500 dark:text-slate-400">Affiliates Found</div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-purple-600" />
              <span className="text-2xl font-bold text-gray-900 dark:text-slate-100">
                {logs.filter(log => log.type === 'success').length}
              </span>
            </div>
          </div>
        </div>

        {/* Geo Filter + Control Buttons */}
        <div className="mt-6 pt-6 border-t border-gray-200 dark:border-slate-700">
          {/* Geo Dropdown */}
          <div className="mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                Select Geo
              </label>
              <select
                value={selectedGeo}
                onChange={(e) => setSelectedGeo(e.target.value)}
                disabled={isRunning}
                className="w-full md:w-64 px-4 py-2.5 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100 focus:ring-2 focus:ring-green-500 focus:border-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="">All Geos</option>
                {availableGeos.map(geo => (
                  <option key={geo} value={geo}>{geo}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex gap-4">
            {!isRunning ? (
              <button
                onClick={() => handleAction('start')}
                disabled={isLoading || isLocalClassifying}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-sm"
              >
                <Play className="w-5 h-5" />
                {isLoading ? 'Starting...' : 'Start Affiliate Finder'}
              </button>
            ) : (
              <button
                onClick={() => handleAction('stop')}
                disabled={isLoading}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-sm"
              >
                <Square className="w-5 h-5" />
                {isLoading ? 'Stopping...' : 'Stop Affiliate Finder'}
              </button>
            )}
          </div>

          {/* Local Re-Classification Buttons */}
          <div className="flex gap-4 mt-4">
            {!isLocalClassifying ? (
              <>
                <button
                  onClick={() => handleLocalClassification('start')}
                  disabled={isLocalLoading || isRunning}
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-sm"
                >
                  <RefreshCw className="w-5 h-5" />
                  {isLocalLoading ? 'Starting...' : 'Re-Classify (Start Fresh)'}
                </button>
                <button
                  onClick={() => handleLocalClassification('resume')}
                  disabled={isLocalLoading || isRunning}
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-sm"
                >
                  <Play className="w-5 h-5" />
                  {isLocalLoading ? 'Resuming...' : 'Resume (Continue)'}
                </button>
              </>
            ) : (
              <button
                onClick={() => handleLocalClassification('stop')}
                disabled={isLocalLoading}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-sm"
              >
                <Square className="w-5 h-5" />
                {isLocalLoading ? 'Stopping...' : 'Stop Re-Classification'}
              </button>
            )}
          </div>

          {/* Affiliate Clean Reclassification Button */}
          <div className="flex gap-4 mt-4">
            {!isAffiliateCleanRunning ? (
              <button
                onClick={() => handleAffiliateClean('start')}
                disabled={isAffiliateCleanLoading || isRunning || isLocalClassifying}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-sm"
              >
                <Sparkles className="w-5 h-5" />
                {isAffiliateCleanLoading ? 'Starting...' : 'Affiliate Clean (Reclassify + SERP Check)'}
              </button>
            ) : (
              <button
                onClick={() => handleAffiliateClean('stop')}
                disabled={isAffiliateCleanLoading}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-sm"
              >
                <Square className="w-5 h-5" />
                {isAffiliateCleanLoading ? 'Stopping...' : 'Stop Affiliate Clean'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Crawler Activity Logs */}
      <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-slate-100">Crawler Activity Logs</h2>
          {logs.length > 0 && (
            <button
              onClick={() => setLogs([])}
              className="text-sm text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200 transition-colors"
            >
              Clear Logs
            </button>
          )}
        </div>

        <div className="space-y-2 max-h-96 overflow-y-auto">
          {logs.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-slate-400">
              <Activity className="w-12 h-12 mx-auto mb-3 text-gray-300 dark:text-slate-600" />
              <p>No activity yet. Start the Affiliate Finder to see logs.</p>
            </div>
          ) : (
            logs.map((log) => (
              <div
                key={log.id}
                className={`p-3 rounded-lg border ${
                  log.type === 'success'
                    ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                    : log.type === 'error'
                    ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                    : log.type === 'warning'
                    ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'
                    : 'bg-gray-50 dark:bg-slate-800 border-gray-200 dark:border-slate-700'
                }`}
              >
                <div className="flex items-start gap-3">
                  {log.type === 'success' ? (
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  ) : log.type === 'error' ? (
                    <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  ) : log.type === 'warning' ? (
                    <Activity className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  ) : (
                    <Activity className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs text-gray-500 dark:text-slate-400 font-mono">{log.time}</span>
                    </div>
                    <p className="text-sm text-gray-900 dark:text-slate-100">{log.message}</p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Local Re-Classification Logs */}
      <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-purple-200 dark:border-purple-800 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-purple-900 dark:text-purple-300">Re-Classification Logs</h2>
          {localLogs.length > 0 && (
            <button
              onClick={() => setLocalLogs([])}
              className="text-sm text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200 transition-colors"
            >
              Clear Logs
            </button>
          )}
        </div>

        <div className="space-y-2 max-h-96 overflow-y-auto">
          {localLogs.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-slate-400">
              <RefreshCw className="w-12 h-12 mx-auto mb-3 text-gray-300 dark:text-slate-600" />
              <p>No re-classification activity yet. Click "Re-Classify (Local HTML)" to start.</p>
            </div>
          ) : (
            localLogs.map((log) => (
              <div
                key={log.id}
                className={`p-3 rounded-lg border ${
                  log.type === 'success'
                    ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                    : log.type === 'error'
                    ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                    : log.type === 'warning'
                    ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'
                    : 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800'
                }`}
              >
                <div className="flex items-start gap-3">
                  {log.type === 'success' ? (
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  ) : log.type === 'error' ? (
                    <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  ) : log.type === 'warning' ? (
                    <Activity className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  ) : (
                    <RefreshCw className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs text-gray-500 dark:text-slate-400 font-mono">{log.time}</span>
                    </div>
                    <p className="text-sm text-gray-900 dark:text-slate-100">{log.message}</p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Affiliate Clean Reclassification Logs */}
      <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-teal-200 dark:border-teal-800 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-teal-900 dark:text-teal-300">Affiliate Clean Logs</h2>
          {affiliateCleanLogs.length > 0 && (
            <button
              onClick={() => setAffiliateCleanLogs([])}
              className="text-sm text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200 transition-colors"
            >
              Clear Logs
            </button>
          )}
        </div>

        <div className="space-y-2 max-h-96 overflow-y-auto">
          {affiliateCleanLogs.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-slate-400">
              <Sparkles className="w-12 h-12 mx-auto mb-3 text-gray-300 dark:text-slate-600" />
              <p>No affiliate clean activity yet. Click "Affiliate Clean" to start reclassification with SERP check.</p>
            </div>
          ) : (
            affiliateCleanLogs.map((log) => (
              <div
                key={log.id}
                className={`p-3 rounded-lg border ${
                  log.type === 'success'
                    ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                    : log.type === 'error'
                    ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                    : log.type === 'warning'
                    ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'
                    : 'bg-teal-50 dark:bg-teal-900/20 border-teal-200 dark:border-teal-800'
                }`}
              >
                <div className="flex items-start gap-3">
                  {log.type === 'success' ? (
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  ) : log.type === 'error' ? (
                    <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  ) : log.type === 'warning' ? (
                    <Activity className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  ) : (
                    <Sparkles className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs text-gray-500 dark:text-slate-400 font-mono">{log.time}</span>
                    </div>
                    <p className="text-sm text-gray-900 dark:text-slate-100">{log.message}</p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Info Card */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-6">
        <div className="flex gap-3">
          <Radar className="w-6 h-6 text-blue-600 dark:text-blue-400 flex-shrink-0" />
          <div>
            <h3 className="font-semibold text-blue-900 dark:text-blue-300 mb-2">About Affiliate Finder (No Cost)</h3>
            <p className="text-sm text-blue-800 dark:text-blue-400">
              Same as Affiliate Finder but uses your local browser instead of Zyte for SERP and HTML fetching.
              Click "Start" to begin the discovery process, and "Stop" to pause it. All activities are logged
              and displayed in real-time below. Re-Classification and Affiliate Clean features are shared with the original Affiliate Finder.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AffiliateFinderNoCost;
